import React, { useContext, useEffect, useReducer } from 'react';
import { changeSubCategoryPositionReducer } from './changeSubCategoryPositionReducer';
import { ChangeSubCategoryPositionActionTypes } from './changeSubCategoryPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeSubCategoryPositionContext,
	changeSubCategoryPositionInitialState,
	TChangeSubCategoryPositionSelectedRestaurant,
	TChangeSubCategoryPositionSelectedItem,
	TTypeOfChangeSubCategoryPositionModalWindow,
} from './changeSubCategoryPositionContext';
import { IGetSubCategoryPositionList, IGetSubCategoryPositionListArguments, ISubCategoryPositionItem } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getSubCategoryPositionList } from 'backend/subcategories';

const ChangeSubCategoryPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeSubCategoryPositionReducer, changeSubCategoryPositionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<
		IGetSubCategoryPositionList,
		IGetSubCategoryPositionListArguments
	>(state.IDSelectedCategory ? getSubCategoryPositionList : null, {
		categoryId: state.IDSelectedCategory || '',
		restaurantId: state.selectedRestaurant?.id || '',
	});

	const setIDCustomer = (value: TChangeSubCategoryPositionSelectedItem) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setRestaurant = (value: TChangeSubCategoryPositionSelectedRestaurant | null) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_RESTAURANT,
			payload: {
				selectedRestaurant: value,
			},
		});
	};

	const setIDCategory = (value: TChangeSubCategoryPositionSelectedItem) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const resetSelectedControls = () =>
		dispatch({ type: ChangeSubCategoryPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: ISubCategoryPositionItem[]) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: ISubCategoryPositionItem[]) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.REORDER_ITEMS,
			payload: {
				newOrder: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeSubCategoryPositionModalWindow) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisible: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
				selectSearchString: value ? '' : state.selectSearchString,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: TChangeSubCategoryPositionSelectedRestaurant[]) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				additionalRestaurants: value,
			},
		});
	};

	const changeSelectSearchString = (value: string) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.CHANGE_SELECT_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	useEffect(() => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: response?.data.subCategoryPositions || [],
			},
		});
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedCategory]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	return (
		<ChangeSubCategoryPositionContext.Provider
			value={{
				setIDCustomer,
				setRestaurant,
				setIDCategory,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeSelectSearchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				selectedRestaurant: state.selectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				selectSearchString: state.selectSearchString,
			}}>
			{children}
		</ChangeSubCategoryPositionContext.Provider>
	);
};

export default ChangeSubCategoryPositionState;
